<template>
  <section class="rules-download-section">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title">League Rules</h2>
        <p class="section-subtitle">Download the official CSI (CueSports International) rules used by The Players League</p>
      </div>

      <div class="rules-card">
        <div class="rules-content">
          <div class="rules-info">
            <div class="document-icon">
              <Icon name="material-symbols:description" />
            </div>
            <div class="document-details">
              <h3 class="document-title">Official CSI Rules</h3>
              <p class="document-description">
                Complete rulebook for CueSports International format used in our league competition
              </p>
              <div class="document-meta">
                <span class="meta-item">
                  <Icon name="material-symbols:calendar-today" />
                  Updated March 2025
                </span>
                <span class="meta-item">
                  <Icon name="material-symbols:file-present" />
                  PDF Format
                </span>
              </div>
            </div>
          </div>
          
          <div class="download-section">
            <a 
              href="/official_rules_of_csi__03112025.pdf" 
              download="Official_Rules_of_CSI_The_Players_League.pdf" 
              class="download-button"
            >
              <Icon name="material-symbols:download" />
              Download Rules PDF
            </a>
            <p class="download-note">
              Essential reading for all league participants
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
// No script needed for this static component
</script>

<style scoped>
.rules-download-section {
  padding: 80px 0;
  background: 
    linear-gradient(180deg, rgba(30, 58, 138, 0.85) 0%, rgba(15, 23, 42, 0.9) 100%),
    url('/images/billiards-3-1920w.webp');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

.rules-download-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 30% 40%, rgba(255, 184, 28, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 70% 60%, rgba(59, 130, 246, 0.06) 0%, transparent 50%);
  z-index: 1;
}

.section-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}

.section-header {
  text-align: center;
  margin-bottom: 48px;
}

.section-title {
  color: var(--gold);
  font-size: 3rem;
  font-family: var(--font-heading);
  font-weight: 700;
  margin-bottom: 16px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  border-radius: 2px;
}

.section-subtitle {
  color: var(--cream);
  font-size: 1.1rem;
  opacity: 0.9;
  line-height: 1.6;
  margin-top: 24px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.rules-card {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.rules-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 184, 28, 0.05), transparent);
  transition: left 0.8s ease;
}

.rules-card:hover {
  border-color: rgba(255, 184, 28, 0.4);
  box-shadow: 0 20px 40px rgba(255, 184, 28, 0.1);
}

.rules-card:hover::before {
  left: 100%;
}

.rules-content {
  color: var(--cream);
}

.rules-info {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 40px;
  padding-bottom: 32px;
  border-bottom: 1px solid rgba(255, 184, 28, 0.2);
}

.document-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  border-radius: 16px;
  color: var(--navy-blue);
  font-size: 2rem;
  flex-shrink: 0;
  box-shadow: 0 6px 20px rgba(255, 184, 28, 0.3);
}

.document-details {
  flex: 1;
}

.document-title {
  margin: 0 0 12px 0;
  font-size: 1.8rem;
  font-family: var(--font-heading);
  font-weight: 600;
  color: var(--gold);
}

.document-description {
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  line-height: 1.6;
  opacity: 0.9;
}

.document-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.95rem;
  color: var(--light-gold);
  font-weight: 500;
}

.meta-item svg {
  font-size: 1rem;
  opacity: 0.8;
}

.download-section {
  text-align: center;
}

.download-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  color: var(--navy-blue);
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 24px rgba(255, 184, 28, 0.3);
  position: relative;
  overflow: hidden;
  margin-bottom: 16px;
}

.download-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s ease;
}

.download-button:hover {
  background: linear-gradient(135deg, var(--light-gold) 0%, var(--gold) 100%);
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(255, 184, 28, 0.4);
}

.download-button:hover::before {
  left: 100%;
}

.download-note {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.8;
  font-style: italic;
  color: var(--cream);
}

/* Responsive Design */
@media (max-width: 768px) {
  .rules-download-section {
    padding: 60px 0;
  }
  
  .section-container {
    padding: 0 16px;
  }
  
  .section-header {
    margin-bottom: 40px;
  }
  
  .section-title {
    font-size: 2.2rem;
  }
  
  .section-subtitle {
    font-size: 1rem;
  }
  
  .rules-card {
    padding: 32px 24px;
  }
  
  .rules-info {
    flex-direction: column;
    text-align: center;
    gap: 20px;
    margin-bottom: 32px;
  }
  
  .document-icon {
    align-self: center;
    width: 60px;
    height: 60px;
    font-size: 1.8rem;
  }
  
  .document-title {
    font-size: 1.6rem;
  }
  
  .document-description {
    font-size: 1rem;
  }
  
  .document-meta {
    justify-content: center;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .rules-download-section {
    padding: 40px 0;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .rules-card {
    padding: 24px 16px;
  }
  
  .download-button {
    width: 100%;
    justify-content: center;
    padding: 18px 24px;
  }
  
  .document-meta {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
